import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTable, usePagination } from 'react-table';
import Header from '../../components/Header/Header';
import Table from '../../components/Table/Table';
import TableTabs from '../../components/TableTabs/TableTabs';
import Empty from '../../components/Empty/Empty';
import Loader from '../../components/Loader/Loader';
import icon from '../../assets/sidebar/uploadWhite.svg';
import dots from '../../assets/dots.svg';
import { iconBucket } from '../../utils/icons';
import formatDate from '../../utils/formatDate';
import fixDateTimezone from '../../utils/fixDateTimezone';
import isUserHaveRole from '../../utils/isUserHaveRole';
import { ROLE_ADMIN } from '../../constants/roles';
import getVinBlacklist from '../../api/vinBlacklist/getVinBlacklist';
import deleteVinFromBlacklist from '../../api/vinBlacklist/deleteVinFromBlacklist';
import AddVinModal from './AddVinModal';

const tableStyle = { backgroundColor: 'white' };

// Машины из базы с этим VIN: на них могут быть сделки, поэтому только показываем.
const CarsCell = ({ cars }) =>
  cars.length === 0
    ? '—'
    : cars.map((car) => (
        <div key={car.carId}>
          <Link to={`/cars/car/${car.carId}/main-data`}>
            {[car.mark, car.model].filter(Boolean).join(' ') || `ID ${car.carId}`}
          </Link>
          {car.stock > 0 ? `, в наличии: ${car.stock}` : ''}
        </div>
      ));

const VinBlacklist = () => {
  const [vins, setVins] = useState([]);
  const [loading, setLoading] = useState(true);
  // Первая загрузка: до неё таблица со своей пагинацией («1 из 1») над спиннером выглядит как сбой
  const [loaded, setLoaded] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
    pageSize: 10,
  });
  const [isFetchAll, setFetchAll] = useState(false);
  const [value, setValue] = useState('');
  const [queryParams, setQueryParams] = useState({});
  const [reload, setReload] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  // Ответ бэка о синхронизации: в контуре-копии список только для чтения, он ведётся в главном контуре
  const [sync, setSync] = useState(null);
  const replica = !!sync?.replica;

  const columns = useMemo(
    () => [
      { Header: 'VIN', accessor: 'vin', width: '25%' },
      { Header: 'Причина', accessor: (row) => row.reason || '—', width: '25%' },
      {
        Header: 'Добавлен',
        accessor: (row) => (row.createdAt ? formatDate(fixDateTimezone(row.createdAt), true) : ''),
        width: '20%',
      },
      {
        Header: 'В базе',
        accessor: 'cars',
        Cell: ({ value: cars }) => <CarsCell cars={cars || []} />,
        disableSortBy: true,
      },
      { Header: <img src={dots} alt="Dots" />, accessor: 'dots' },
    ],
    []
  );

  const table = useTable(
    {
      columns,
      data: vins,
      initialState: { pageSize: 10 },
      manualPagination: true,
      pageCount: pagination.totalPages,
      page: pagination.page - 1,
      disableSortRemove: true,
    },
    usePagination
  );
  const { page } = table;

  useEffect(() => {
    if (isFetchAll) return;
    setLoading(true);
    getVinBlacklist({ page: pagination.page, pageSize: pagination.pageSize, ...queryParams })
      .then((res) => {
        setVins(res?.data?.vins || []);
        setSync(res?.data?.sync || null);
        // Читаем до setPagination: исключение внутри обновителя случается при рендере и роняет страницу
        const next = res?.data?.pagination;
        if (next) {
          setPagination((prev) => ({ ...next, page: next.pageNumber, pageSize: prev.pageSize }));
        }
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
        setLoaded(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.pageSize, queryParams, isFetchAll, reload]);

  const popover = useMemo(
    () => [
      {
        title: 'Удалить',
        icon: iconBucket(),
        onClick: (row) =>
          deleteVinFromBlacklist(row?.id)
            .then(() => setReload((n) => n + 1))
            .catch(() => {}),
      },
    ],
    []
  );

  const search = (text) => {
    setQueryParams({ all: (text || '').trim() });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Бэк тоже проверяет роль; здесь — чтобы остальным не показывать страницу с ошибками.
  if (!isUserHaveRole(ROLE_ADMIN)) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <Header title="Чёрный список VIN" icon={icon} />
      <br />
      <br />
      <br />
      <br />
      <TableTabs
        setFilter={search}
        value={value}
        setValue={setValue}
        navigate={() => setAddOpen(true)}
        button="Добавить"
        inputPlaceholder="Быстрый поиск по VIN"
        advancedSearch={false}
        // Копия чужого списка: кнопки «Добавить» нет
        addButton={!replica}
      />
      {replica && (
        <p style={{ margin: '10px 0 0', fontSize: 14, color: '#8b8b8b' }}>
          Список ведётся в основном контуре.{' '}
          {sync.lastSyncAt ? `Обновлён: ${formatDate(fixDateTimezone(sync.lastSyncAt), true)}.` : 'Ещё не обновлялся.'}
          {sync.error ? ` Не удалось обновить: ${sync.error}.` : ''}
        </p>
      )}
      <br />
      {!loaded ? (
        <Loader />
      ) : !page.length && !loading ? (
        <Empty />
      ) : (
        <Table
          table={table}
          all={'VIN'}
          style={tableStyle}
          pagination
          setPagination={setPagination}
          setFetchAll={setFetchAll}
          isFetchAll={isFetchAll}
          setData={setVins}
          p={pagination}
          loading={loading}
          setLoading={setLoading}
          queryParams={queryParams}
          api={'/api/crm/vin-blacklist'}
          response={'vins'}
          popover={replica ? undefined : popover}
          edit={!replica}
        />
      )}
      <AddVinModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onDone={() => {
          setAddOpen(false);
          setPagination((prev) => ({ ...prev, page: 1 }));
          setReload((n) => n + 1);
        }}
      />
    </>
  );
};

export default VinBlacklist;
